/**
 * HomePage — MADE-303
 *
 * Public landing page for offshore-sails.com.
 * Replaces the ComingSoon splash. Keeps the "Staff Login" path reachable via nav.
 */

import React from 'react';
import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import { Water as WaterIcon, Verified as VerifiedIcon, WbSunny as WbSunnyIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { MarketingLayout } from './MarketingLayout';

const FEATURES = [
  {
    icon: <WaterIcon sx={{ fontSize: 40, color: '#FFC726' }} />,
    title: 'Built for Demanding Conditions',
    body: 'Every sail we make is engineered for the realities of serious sailing: sustained loads, intense UV exposure, and the demanding conditions of offshore passages, wherever in the world they happen.',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 40, color: '#FFC726' }} />,
    title: 'Industry-Leading Warranty',
    body: 'Our flagship Offshore sails carry the most comprehensive warranty in the industry: five years or 50,000 nautical miles, covering both workmanship and materials, without exception.',
  },
  {
    icon: <WbSunnyIcon sx={{ fontSize: 40, color: '#FFC726' }} />,
    title: 'Proprietary UV Protection',
    body: 'Our Harris Paints UV-protection sealant guards every seam against UV degradation from the first day of service, a critical advantage wherever a sail spends its life in the sun.',
  },
];

export const HomePage: React.FC = () => {
  return (
    <MarketingLayout pageTitle="Caribbean Sailmakers">

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          borderBottom: '1px solid rgba(0,38,128,0.08)',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems="center"
            spacing={{ xs: 6, md: 10 }}
          >
            {/* Text */}
            <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                component="h1"
                variant="h2"
                sx={{
                  color: '#002680',
                  fontWeight: 700,
                  mb: 2.5,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  lineHeight: 1.15,
                }}
              >
                Sails Built in the&nbsp;Caribbean.
                <Box
                  component="span"
                  sx={{ display: 'block', color: '#444', fontWeight: 500, fontSize: '0.62em', mt: 1 }}
                >
                  At home around the world.<br />
                  Engineered for tropical UV and the demands of serious offshore sailing.
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: '#555', mb: 4, maxWidth: 480, mx: { xs: 'auto', md: 0 }, lineHeight: 1.75 }}
              >
                Offshore Sails is one of the only sailmakers in the world building Dacron
                sails in luff lengths up to 125 feet. Based in Barbados, we specialize in
                sailboats over 55 feet, including charter and trade vessels, while building
                significant numbers of sails for smaller boats too. The same durability and
                UV-conscious design choices we developed for the largest sails are standard
                on every sail we make.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent={{ xs: 'center', md: 'flex-start' }}
              >
                <Button
                  component={RouterLink}
                  to="/os/sails"
                  variant="contained"
                  size="large"
                  data-testid="hero-cta-sails"
                  sx={{
                    bgcolor: '#002680',
                    color: 'white',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': { bgcolor: '#001a60' },
                  }}
                >
                  View Our Sails
                </Button>
                <Button
                  component={RouterLink}
                  to="/os/contact"
                  variant="outlined"
                  size="large"
                  data-testid="hero-cta-contact"
                  sx={{
                    borderColor: '#FFC726',
                    color: '#002680',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': { bgcolor: 'rgba(255,199,38,0.08)', borderColor: '#e6a800' },
                  }}
                >
                  Get a Quote
                </Button>
              </Stack>
            </Box>

            {/* Logo mark */}
            <Box
              sx={{
                flex: '0 0 auto',
                maxWidth: { xs: 280, md: 380 },
                width: '100%',
                p: { xs: 4, md: 5 },
                bgcolor: 'white',
                borderRadius: 3,
                border: '1px solid rgba(0,38,128,0.10)',
                boxShadow: '0 8px 32px rgba(0,38,128,0.07)',
              }}
            >
              <Box
                component="img"
                src="/os/Offshore Sails-Horizontal-MainColor.svg"
                alt="Offshore Sails"
                sx={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ── Gold divider ───────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#FFC726', height: 4 }} />

      {/* ── Feature highlights ─────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#fafbff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            sx={{ color: '#002680', fontWeight: 700, mb: 1.5, textAlign: 'center' }}
          >
            Why Offshore Sails
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#666', textAlign: 'center', mb: 6, maxWidth: 560, mx: 'auto' }}
          >
            Nearly four decades of sailmaking experience, applied to every sail we build,
            from the Americas and Europe to the rest of the world.
          </Typography>

          <Grid container spacing={4}>
            {FEATURES.map(({ icon, title, body }) => (
              <Grid key={title} item xs={12} md={4}>
                <Box
                  data-testid={`feature-${title.replace(/\s+/g, '-').toLowerCase()}`}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 3,
                    p: 4,
                    height: '100%',
                    border: '1px solid rgba(0,38,128,0.08)',
                    boxShadow: '0 2px 12px rgba(0,38,128,0.05)',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 6px 24px rgba(0,38,128,0.10)' },
                  }}
                >
                  <Box mb={2}>{icon}</Box>
                  <Typography variant="h6" sx={{ color: '#002680', fontWeight: 700, mb: 1.5 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.75 }}>
                    {body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA strip ──────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#002680', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
              Ready to talk about your sails?
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 480 }}>
              Tell us about your boat and the sails you need. We will get back to you
              with honest advice and a fair quote.
            </Typography>
            <Button
              component={RouterLink}
              to="/os/contact"
              variant="contained"
              size="large"
              data-testid="bottom-cta-contact"
              sx={{
                bgcolor: '#FFC726',
                color: '#002680',
                fontWeight: 700,
                px: 5,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: '#e6a800' },
              }}
            >
              Get in Touch
            </Button>
          </Stack>
        </Container>
      </Box>

    </MarketingLayout>
  );
};
